import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { I18nProvider, useI18n } from './i18n'
import { useAuth, useCan, useModule, type Module, type SessionUser, type VenueSummary } from './store/auth'
import { api } from './api'
import { LoadingBlock } from './components/ui'
import { useHomePath } from './components/nav'
import {
  appPath,
  appPathVariants,
  publicSegmentVariants,
  type AppPath,
} from './lib/paths'

/*
 * Le back-office est chargé à la demande, la billetterie publique ne l'est
 * pas.
 *
 * Ce sont deux applications avec deux publics : d'un côté une équipe qui
 * ouvre l'outil le matin et le garde ouvert, de l'autre un visiteur qui
 * arrive depuis un lien et décide en quelques secondes. Lui faire télécharger
 * le calendrier, le fichier client et le roster pour acheter deux billets est
 * du temps de chargement prélevé exactement là où il coûte une vente.
 */
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })))
const Shell = lazy(() => import('./components/Shell').then((m) => ({ default: m.Shell })))
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const TeamPage = lazy(() => import('./pages/TeamPage').then((m) => ({ default: m.TeamPage })))
const MyRosterPage = lazy(() =>
  import('./pages/MyRosterPage').then((m) => ({ default: m.MyRosterPage })),
)
const CalendarPage = lazy(() =>
  import('./pages/CalendarPage').then((m) => ({ default: m.CalendarPage })),
)
const BookingsPage = lazy(() =>
  import('./pages/BookingsPage').then((m) => ({ default: m.BookingsPage })),
)
const CustomersPage = lazy(() =>
  import('./pages/CustomersPage').then((m) => ({ default: m.CustomersPage })),
)
const OnboardingPage = lazy(() =>
  import('./pages/public/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
)
const UnsubscribePage = lazy(() =>
  import('./pages/public/UnsubscribePage').then((m) => ({ default: m.UnsubscribePage })),
)
const OnboardingsPage = lazy(() =>
  import('./pages/OnboardingsPage').then((m) => ({ default: m.OnboardingsPage })),
)
const OpsPage = lazy(() => import('./pages/OpsPage').then((m) => ({ default: m.OpsPage })))
const RoomsPage = lazy(() => import('./pages/RoomsPage').then((m) => ({ default: m.RoomsPage })))
const WelcomePage = lazy(() => import('./pages/WelcomePage').then((m) => ({ default: m.WelcomePage })))
const AnalyticsPage = lazy(() =>
  import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })),
)
const ScanPage = lazy(() => import('./pages/ScanPage').then((m) => ({ default: m.ScanPage })))
const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })))
const ScreenPage = lazy(() => import('./pages/public/ScreenPage').then((m) => ({ default: m.ScreenPage })))
const SitePage = lazy(() =>
  import('./pages/public/SitePage').then((m) => ({ default: m.SitePage })),
)
const ProfilePage = lazy(() =>
  import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
)
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const ExperiencesPage = lazy(() =>
  import('./pages/ExperiencesPage').then((m) => ({ default: m.ExperiencesPage })),
)
/* La console du studio est chargée à la demande comme les autres : elle ne
   concerne qu'une poignée de comptes, et son bundle n'a pas à peser sur le
   premier rendu d'un opérateur. */
const AdminPage = lazy(() =>
  import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })),
)
const BookPage = lazy(() =>
  import('./pages/public/BookPage').then((m) => ({ default: m.BookPage })),
)

/**
 * Au démarrage on revalide le jeton persisté en rechargeant le profil. Un
 * jeton en stockage local peut être expiré, ou appartenir à un compte dont
 * l'accès a été retiré ; l'interface ne doit pas se peindre avant de le
 * savoir.
 */
function useBootstrap() {
  const { token, signOut, setVenues, setUser } = useAuth()
  const { setLocale } = useI18n()
  const [ready, setReady] = useState(!token)

  useEffect(() => {
    if (!token) {
      setReady(true)
      return
    }
    let cancelled = false
    api<{ user: SessionUser; venues: VenueSummary[]; mode?: 'cloud' | 'room' }>('/auth/me')
      .then(({ user, venues, mode }) => {
        if (cancelled) return
        setVenues(venues)
        /* Facultatif à la lecture : une instance plus ancienne que ce champ
           ne le renvoie pas, et elle est forcément un nuage. */
        useAuth.setState({ mode: mode ?? 'cloud' })
        /*
         * Le profil est rechargé, pas seulement les lieux : le nom, la photo
         * et la langue peuvent avoir changé depuis un autre poste, et la barre
         * latérale les affiche.
         */
        setUser(user)
        /*
         * La langue du COMPTE s'applique au démarrage.
         *
         * Elle est enregistrée sur la personne précisément pour la suivre d'un
         * poste à l'autre ; ne pas la relire ici aurait laissé le stockage
         * local décider, et le réglage aurait paru sans effet dès qu'on ouvre
         * Portage ailleurs. Après le démarrage, c'est le choix local qui gagne
         * — et il est réécrit sur le compte.
         */
        if (user.uiLang === 'fr' || user.uiLang === 'en') setLocale(user.uiLang)
      })
      .catch(() => {
        if (!cancelled) signOut()
      })
      .finally(() => {
        if (!cancelled) setReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [token, signOut, setVenues, setUser, setLocale])

  return ready
}

function BackOffice() {
  const token = useAuth((s) => s.token)
  const ready = useBootstrap()

  if (!ready) return <LoadingBlock />
  if (!token) return <LoginPage />

  return (
    <Shell>
      {/*
        Chaque écran est servi sur SES DEUX chemins, français et anglais.

        Les URL étaient en français dans un produit bilingue : un opérateur
        anglophone lisait « Bookings » sur `/reservations`. Le chemin ÉMIS suit
        maintenant la langue de l'interface (voir `Shell.tsx`), mais l'autre
        variante reste servie — un signet, un lien collé dans une conversation
        ou un onglet resté ouvert continuent de fonctionner.
      */}
      <Routes>
        {SCREENS.map(({ key, element }) =>
          appPathVariants(key).map((path) => (
            <Route
              key={path}
              path={path}
              /*
               * La racine est gardée : le tableau de bord demande
               * `sessions:read`, que le preset `scan` n'a pas. Une tablette qui
               * se connectait tombait sur « quelque chose s'est mal passé »,
               * avec à côté une barre latérale parfaitement correcte — filtrer
               * la navigation ne suffit pas, puisque personne n'a cliqué pour
               * arriver là.
               */
              element={
                key === 'dashboard' || key === 'today' ? (
                  <Home>{element}</Home>
                ) : SCREEN_MODULES[key] ? (
                  <ModuleGate module={SCREEN_MODULES[key]}>{element}</ModuleGate>
                ) : (
                  element
                )
              }
            />
          )),
        )}
        <Route path="*" element={<Home>{null}</Home>} />
      </Routes>
    </Shell>
  )
}

/**
 * La racine, ou le premier écran que cette personne peut ouvrir.
 *
 * Sert aussi de fourre-tout : un chemin inconnu renvoyait vers `/`, ce qui
 * ramenait le même écran d'erreur pour les presets étroits.
 */
function Home({ children }: { children: ReactNode }) {
  const home = useHomePath()
  /* La journée et le tableau de bord sont de la billetterie (28/10). */
  const canReadSessions = useCan('sessions:read')
  const billetterie = useModule('billetterie')
  const canSeeDashboard = canReadSessions && billetterie
  /*
   * EN SALLE, PAS DE TABLEAU DE BORD (Showroom, 01/10) : il interroge
   * `/api/group`, que la salle ne monte pas, et l'on atterrissait après la
   * connexion sur « Quelque chose a échoué ». La salle ouvre sur son premier
   * écran — l'exploitation.
   */
  const roomMode = useAuth((state) => state.mode) === 'room'
  if (!roomMode && canSeeDashboard && children) return <>{children}</>
  return <Navigate to={home} replace />
}

/*
 * LES ÉCRANS D'UN MODULE (28/10). La navigation les cache déjà ; un signet ou
 * un lien collé y mène encore, et l'écran se peindrait sur des requêtes
 * refusées. On renvoie à l'accueil, comme un écran de la salle ouvert au nuage.
 */
const SCREEN_MODULES: Partial<Record<AppPath, Module>> = {
  group: 'billetterie',
  calendar: 'billetterie',
  bookings: 'billetterie',
  scan: 'billetterie',
  customers: 'marketing',
  myRoster: 'equipe',
}

function ModuleGate({ module, children }: { module: Module; children: ReactNode }) {
  const on = useModule(module)
  const home = useHomePath()
  if (on) return <>{children}</>
  return <Navigate to={home} replace />
}

/** L'ancienne page Casques (09/10) : ses casques vivent dans Salles. */
function ToRooms() {
  const { locale } = useI18n()
  return <Navigate to={appPath('rooms', locale)} replace />
}

/** Un écran de la salle ouvert au nuage (un signet, un lien ancien) : vers l'accueil. */
function RoomOnly({ children }: { children: ReactNode }) {
  const roomMode = useAuth((state) => state.mode) === 'room'
  const home = useHomePath()
  if (roomMode) return <>{children}</>
  return <Navigate to={home} replace />
}

/*
 * La table des écrans du back-office.
 *
 * Elle existe pour que les chemins ne soient déclarés qu'une fois : la clé
 * relie une route à son entrée de `APP_PATHS`, et `appPathVariants` en dérive
 * les deux langues. Écrire les routes à la main obligeait à penser aux deux
 * variantes à chaque ajout, et on n'y pense jamais.
 */
const SCREENS: Array<{ key: AppPath; element: ReactNode }> = [
  /*
   * La racine est le TABLEAU DE BORD — où suis-je, où puis-je aller — et
   * « Aujourd'hui » est la journée d'un LIEU. C'était le même écran, ce qui
   * marchait pour une salle unique et cachait les dix autres pour tous les
   * comptes qui en ont plusieurs.
   */
  { key: 'dashboard', element: <HomePage /> },
  { key: 'today', element: <DashboardPage /> },
  /* `/mes-lieux` sert le même écran : c'est le tableau de bord sous son
     ancien nom, et des signets y pointent. */
  { key: 'group', element: <HomePage /> },
  { key: 'calendar', element: <CalendarPage /> },
  /* Les postes ET le planning : deux onglets d'un même écran, parce qu'on
     embauche, on donne un poste et on met au planning d'une seule traite. */
  { key: 'team', element: <TeamPage /> },
  /* Hors navigation, comme le compte : on y arrive par son nom en pied de
     barre latérale. C'est de la personne, pas du lieu. */
  { key: 'myRoster', element: <MyRosterPage /> },
  { key: 'bookings', element: <BookingsPage /> },
  { key: 'customers', element: <CustomersPage /> },
  /* `campaigns` : masqué avec son entrée de menu, voir nav.tsx. */
  { key: 'analytics', element: <AnalyticsPage /> },
  { key: 'onboarding', element: <OnboardingsPage /> },
  /* L'exploitation se pilote dans la salle, jamais au nuage (voir nav.tsx). */
  { key: 'ops', element: <RoomOnly><OpsPage /></RoomOnly> },
  /* LA PAGE CASQUES EST PARTIE DANS SALLES (Quentin, 09/10) : un signet
     ancien y mène. */
  { key: 'fleet', element: <ToRooms /> },
  { key: 'rooms', element: <RoomsPage /> },
  { key: 'welcome', element: <WelcomePage /> },
  { key: 'scan', element: <ScanPage /> },
  { key: 'settings', element: <SettingsPage /> },
  /* Le compte, hors navigation : on y arrive par son nom en pied de barre,
     là où tout outil moderne le place. */
  { key: 'profile', element: <ProfilePage /> },
  { key: 'experiences', element: <ExperiencesPage /> },
  /* La console du studio. Le composant refuse lui-même les non-superadmins,
     et `/api/admin` la refuse pour de bon. */
  { key: 'studio', element: <AdminPage /> },
]

export function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <Suspense fallback={<LoadingBlock />}>
          <Routes>
            {/*
              La billetterie publique vit sous /v/:slug et ne partage rien avec
              le back-office : ni coque, ni authentification, ni tenancy par
              en-tête. C'est une application différente, servie depuis la même
              origine mais depuis son propre morceau de code.
            */}
            {/* Le désabonnement est AVANT la route fourre-tout de la venue,
                sinon `/v/:slug/*` l'avale et le lien ouvre la billetterie. */}
            {/*
              Les DEUX variantes de chaque segment public, et ici ce n'est pas
              négociable : le QR-code d'enregistrement est imprimé et collé sur
              un mur, et les liens de désabonnement sont dans des courriels
              déjà partis. Renommer sans garder l'ancien casse du papier.
            */}
            {publicSegmentVariants('unsubscribe').map((segment) => (
              <Route
                key={segment}
                path={`/v/:slug/${segment}/:token`}
                element={<UnsubscribePage />}
              />
            ))}
            {/* L'enregistrement : même page pour la borne (?borne=jeton), le
                lien du courriel (?ref=ODY-…) et le QR-code du mur. */}
            {publicSegmentVariants('onboarding').map((segment) => (
              <Route
                key={segment}
                path={`/v/:slug/${segment}`}
                element={<OnboardingPage />}
              />
            ))}
            {/* Le site d'une expérience. En production il arrive par son
                nom d'hôte ; `/site?host=` permet de le viser en dev, où il n'y
                a pas de DNS joker. */}
            {/* L'écran d'un lieu : un jeton dans l'URL, rien d'autre. Il est
                AVANT la route fourre-tout du back-office, sinon la coque
                l'avalerait et demanderait de se connecter à un téléviseur. */}
            <Route path="/ecran/:token" element={<ScreenPage />} />
            <Route path="/screen/:token" element={<ScreenPage />} />
            <Route path="/site" element={<SitePage />} />
            <Route path="/v/:slug/*" element={<BookPage />} />
            <Route path="*" element={<BackOffice />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </I18nProvider>
  )
}
